import { DSQConflitingMedication } from "@/Common/Api/Dsq/Dto/Responses/DSQConflictingMedications";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { MyleDSQMedicationItem } from "@/Common/Models/DSQ/MyleDSQMedicationItem";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { PrescriptionMedicationCheckBoxes, PrescriptionMedicationKendoComboBoxType, PrescriptionMedicationTextBoxes } from "@/Common/Models/Patient/PatientEnums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { MedicationListType } from "@/Patient/Components/Medication/PatientPrescriptionTab";
import { MedicalItemMedicationAndPrescriptionDosage } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescriptionDosage";
import { MedicationDosageDisplayMode, MedicationDosageDisplayModeDurationUnit, MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { MedicationResponse } from "@/Patient/Models/Medication/ResponseModels/MedicationResponse";
import { PatientPrescriptionAdjustMedicationPopupModel } from "@/Patient/Models/PatientPrescriptionAdjustMedicationPopupModel";
import { IPrescriptionTabModel } from "@/Patient/Models/UI/Medication/IPrescriptionTabModel";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientPrescriptionTabHandler extends BaseOperationHandler {
    constructor() {
        super();
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.FavoriteMedicationSelected, this.favoriteMedicationSelected);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.MedicationSelected, this.MedicationSelected);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.RemoveMedication, this.RemoveMedication);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.SetMedicationModelDurationMode, this.SetMedicationModelDurationMode);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.AddDosage, this.AddDosage);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.DeleteDosage, this.DeleteDosage);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.ComboBoxChange, this.ComboBoxChange);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.TextBoxChange, this.TextBoxChange);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.CheckBoxChange, this.CheckBoxChange);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.StartDateChange, this.StartDateChange);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.Prescribe, this.ValidateAndPrescribe);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.CancelPrescribeAction, this.CancelPrescribeAction);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.ReorderMedicationDragAndDrop, this.ReorderMedicationDragAndDrop);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.ToggleEditPreviewMedicationModel, this.ToggleEditPreviewMedicationModel);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.NewEmptyRx, this.NewEmptyRx);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.LeftPaneActiveMed_Renew, this.LeftPaneActiveMed_Renew);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.LeftPaneActiveMed_Adjust, this.LeftPaneActiveMed_Adjust);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.LeftPaneActiveMed_RenewAll, this.LeftPaneActiveMed_RenewAll);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.AdjustMedicationPopup_Cancel, this.AdjustMedicationPopup_Close);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.AdjustMedicationPopup_Continue, this.AdjustMedicationPopup_Continue);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.MedicationDetails_GetVigilanceMonographie, this.MedicationDetails_GetVigilanceMonographie);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.GetVigilanceAnalysis, this.GetVigilanceAnalysis);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.ToggleAddAsFavorite, this.ToggleAddAsFavorite);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.ToggleSelectedMedicationList, this.ToggleSelectedMedicationList);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.DSQMedsLoaded, this.DSQMedsLoaded);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.DSQMedsLoading, this.DSQMedsLoading);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.ExternalSearchMedicationTextChange, this.ExternalSearchMedicationTextChange);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.CloseActiveMedConflicsPopup, this.CloseActiveMedConflicsPopup);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.UpdateAllRenewalsNumberChange, this.updateAllRenewalsNumber);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.SaveGeneralInstructions, this.SaveGeneralInstructions);
        this.RegisterOperation(LocalPatientOperations.PrescriptionTab.SaveIsHeaderExpanded, this.saveIsHeaderExpanded);
    }

    private CloseActiveMedConflicsPopup(request: LocalEvent): void {
        let prescriptionTabModel = patientStore.PatientPrescriptionTabModels[request.PatientId];
        prescriptionTabModel = prescriptionTabModel.set("showMyleActiveMedicationConflicPopup", false);
        patientStore.PatientPrescriptionTabModels[request.PatientId] = prescriptionTabModel;
    }

    private ExternalSearchMedicationTextChange(request: LocalEvent): void {
        let newText = request.Get<string>("text");

        patientStore.PatientPrescriptionTabModels[request.PatientId] = patientStore.PatientPrescriptionTabModels[request.PatientId].set("SearchText", newText);
        patientStore.PatientPrescriptionTabModels[request.PatientId] = patientStore.PatientPrescriptionTabModels[request.PatientId].set("SearchTextEventId", sessionStore.newId().toString());
    }

    private DSQMedsLoaded(request: LocalEvent): void {
        let success = request.Get<boolean>("Success");
        let DSQ_Meds = request.Get<MyleDSQMedicationItem[]>("Meds");

        if (success == true) {
            let MedicationModels = DSQ_Meds.map(x => MedicationModel.FromMyleDSQMedicationItem(x));

            patientStore.PatientPrescriptionTabModels[request.PatientId] = patientStore.PatientPrescriptionTabModels[request.PatientId].set("DSQMedicationModels", MedicationModels.toImmutable());
            patientStore.PatientPrescriptionTabModels[request.PatientId] = patientStore.PatientPrescriptionTabModels[request.PatientId].set("selectedMedicationList", MedicationListType.DSQ);
        }

        sessionStore.hideGlobalOverlay();
    }

    private DSQMedsLoading(request: LocalEvent): void {
        let isLoading = request.Get<boolean>("isLoading");
        patientStore.PatientPrescriptionTabModels[request.PatientId] = patientStore.PatientPrescriptionTabModels[request.PatientId].set("isDSQLoading", isLoading);

        if (isLoading == true)
            sessionStore.showGlobalOverlay(true);
        else
            sessionStore.hideGlobalOverlay();
    }

    private ToggleSelectedMedicationList(request: LocalEvent): void {
        let selectedMedicationListType = request.Get<MedicationListType>("selectedMedicationListType");
        patientStore.PatientPrescriptionTabModels[request.PatientId] = patientStore.PatientPrescriptionTabModels[request.PatientId].set("selectedMedicationList", selectedMedicationListType);
    }

    private ToggleAddAsFavorite(request: LocalEvent): void {
        var IsFavorite = patientStore.PatientPrescriptionTabModels[request.PatientId].get("IsFavorite");
        patientStore.PatientPrescriptionTabModels[request.PatientId] = patientStore.PatientPrescriptionTabModels[request.PatientId].set("IsFavorite", !IsFavorite);
    }

    private MedicationDetails_GetVigilanceMonographie(request: LocalEvent, store: PatientStore): void {
        let codeGen = request.Get<string>("item");
        let medicationSearchResponseId = request.Get<string>("id");
        store.ActionsPrescriptionTab.MedfarDictionnary_GetVigilanceMonographie(codeGen, medicationSearchResponseId);
    }

    private AdjustMedicationPopup_Continue(request: LocalEvent, store: PatientStore): void {
        const response = request.Get<PatientPrescriptionAdjustMedicationPopupModel>("item");
        const baseMed = response.baseMed;
        const newMed = response.newMed;
        const status = response.selectedCode;
        const ceaseReason = response.ceaseReason;
        const ceaseDate: Date = response.ceaseDate;
        const discontinueReasonLookupId = response.discontinueReasonLookupId;

        if (baseMed != null) {
            let pendingprescriptionitem = this.AdjustMed(request.PatientId, baseMed, status, ceaseDate, ceaseReason, discontinueReasonLookupId);
            if (newMed != null) {
                newMed.PrescriptionTab_OriginalMedication = pendingprescriptionitem;

                // newMed.Model.Dosages = [].toImmutable();
                // newMed.CreateFakeDosage();
                // newMed.AddNewDosage();

                this.AddMedicationModel(request.PatientId, newMed);
            }
            else {
                this.AddMedicationModel(request.PatientId, pendingprescriptionitem);
            }
        }

        store.PatientPrescriptionTabModels[request.PatientId] = store.PatientPrescriptionTabModels[request.PatientId].set("ShowAdjustMedicationPopup", false);
    }

    public AdjustMed(patientId: string, medication: MedicationModel, status: string, ceaseDate: Date, ceaseReason: string, discontinueReasonLookupId: string | null): MedicationModel {
        this.ChangeMedicationPendingStatus(medication.Model.Id, patientId, status, false);

        const ceaseEmployeeId = sessionStore.getCurrentEmployee().ContactFacetId;
        const ceaseEncounterId = patientStore.PatientPrescriptionTabModels[patientId].get("EncounterId");
        const adjustedMedication = MedicationModel.setAdjustMedication(medication, status, ceaseDate, ceaseReason, discontinueReasonLookupId, ceaseEmployeeId, ceaseEncounterId);

        return adjustedMedication;
    }

    private AdjustMedicationPopup_Close(request: LocalEvent, store: PatientStore): void {
        store.PatientPrescriptionTabModels[request.PatientId] = store.PatientPrescriptionTabModels[request.PatientId].set("ShowAdjustMedicationPopup", false);
    }

    private OpenAndResetAdjustWindow(patientId: string): void {
        patientStore.PatientPrescriptionTabModels[patientId] = patientStore.PatientPrescriptionTabModels[patientId].set("AdjustMedication", null);
        patientStore.PatientPrescriptionTabModels[patientId] = patientStore.PatientPrescriptionTabModels[patientId].set("ShowAdjustMedicationPopup", true);
    }

    private LeftPaneActiveMed_Adjust(request: LocalEvent, store: PatientStore): void {
        let fromMedicationId = request.Get<string>("id");
        let medication = PatientPrescriptionTabHandler.GetMedicationById(fromMedicationId, request.PatientId);
        if (medication != null) {
            this.OpenAndResetAdjustWindow(request.PatientId);
            store.PatientPrescriptionTabModels[request.PatientId] = store.PatientPrescriptionTabModels[request.PatientId].set("AdjustMedication", medication);
        }
    }

    private LeftPaneActiveMed_RenewAll(request: LocalEvent, store: PatientStore): void {
        let medicationListtype = store.PatientPrescriptionTabModels[request.PatientId].get("selectedMedicationList");
        let DSQMeds = store.PatientPrescriptionTabModels[request.PatientId].get("DSQMedicationModels");
        let ActiveSummaryMeds = store.PatientMedicalSummaryModels[request.PatientId].get("MedicalItemsMedicationAndPrescription").filter(x => x.Model.SummaryDisplayStatus == 1 || x.Model.SummaryDisplayStatus == 2);

        if (medicationListtype == MedicationListType.DSQ) {
            if (DSQMeds == null)
                return;

            DSQMeds.forEach((x: MedicationModel) => {
                if (/* IsActive && */ x.Model.PrescriptionTab_IsRenew != true && x.Model.PrescriptionTab_PendingStatus == null) {
                    let renewedModel = x.Renew();
                    renewedModel.Model.DSQRenew_MyleActiveMedicationIdsConflic = ActiveSummaryMeds.filter(activeMed => activeMed.Model.CodeGen == renewedModel.Model.CodeGen).map(activemed => activemed.Model.Id).toArray();

                    this.AddMedicationModel(request.PatientId, renewedModel, true);
                    this.ChangeMedicationPendingStatus(x.Model.Id, request.PatientId, null, true);
                }
            });
        }
        else {
            ActiveSummaryMeds.forEach((x: MedicationModel) => {
                if (/* IsActive && */ x.Model.PrescriptionTab_IsRenew != true && x.Model.PrescriptionTab_PendingStatus == null) {
                    this.AddMedicationModel(request.PatientId, x.Renew(), true);
                    this.ChangeMedicationPendingStatus(x.Model.Id, request.PatientId, null, true);
                }
            });
        }
    }

    private LeftPaneActiveMed_Renew(request: LocalEvent, store: PatientStore): void {
        let orginalMedicationId = request.Get<string>("id");
        let activeSummaryMeds = store.PatientMedicalSummaryModels[request.PatientId].get("MedicalItemsMedicationAndPrescription").filter(x => x.Model.SummaryDisplayStatus == 1 || x.Model.SummaryDisplayStatus == 2);
        let originalMed = PatientPrescriptionTabHandler.GetMedicationById(orginalMedicationId, request.PatientId);
        let renewedMed = originalMed.Renew();

        if (renewedMed.Model.isDSQMed_NotImported == true)
            renewedMed.Model.DSQRenew_MyleActiveMedicationIdsConflic = activeSummaryMeds.filter(activeMed => activeMed.Model.CodeGen == originalMed.Model.CodeGen).map(activemed => activemed.Model.Id).toArray();

        this.AddMedicationModel(request.PatientId, renewedMed);
        this.ChangeMedicationPendingStatus(orginalMedicationId, request.PatientId, null, true);
    }

    private NewEmptyRx(request: LocalEvent): void {
        this.AddMedicationModel(request.PatientId, MedicationModel.FromDefaultModel(true, false));
    }

    private ToggleEditPreviewMedicationModel(request: LocalEvent): void {
        let medicationId = request.Get<string>("id");

        this.MutatePrescriptionTabMedications(medicationId, request.PatientId, (x: MedicationModel) => {
            x.IsInEditMode = !x.IsInEditMode && x.Model.PrescriptionTab_PendingStatus == null;
        });
    }

    private ReorderMedicationDragAndDrop(request: LocalEvent): void {
        var itemId = request.Get<string>("itemId");
        var newIndex = request.Get<number>("itemIndex"); // new zero based position in list
        var currentIndex = this.GetPendingMedicationById(itemId, request.PatientId).Model.EMRTemplateSortIndex;

        this.MutatePrescriptionTabMedications("ALL", request.PatientId, (x: MedicationModel) => {
            if (x.Model.EMRTemplateSortIndex >= newIndex && x.Model.EMRTemplateSortIndex < currentIndex && x.Model.Id != itemId)
                x.EMRTemplateSortIndexSet(x.Model.EMRTemplateSortIndex + 1);
            else if (x.Model.EMRTemplateSortIndex > currentIndex && x.Model.EMRTemplateSortIndex <= newIndex && x.Model.Id != itemId)
                x.EMRTemplateSortIndexSet(x.Model.EMRTemplateSortIndex - 1);
        });
        this.MutatePrescriptionTabMedications(itemId, request.PatientId, (x: MedicationModel) => {
            x.EMRTemplateSortIndexSet(newIndex);
        });

        let prescriptionTabModel = patientStore.PatientPrescriptionTabModels[request.PatientId]; // Get Patient Model
        let MedicationModels = prescriptionTabModel.get("MedicationModels"); // Get Old List
        let newMedicationModels = MedicationModels.sort((cur, next) => {
            if (cur.Model.EMRTemplateSortIndex == next.Model.EMRTemplateSortIndex)
                return 0;
            else
                return (cur.Model.EMRTemplateSortIndex > next.Model.EMRTemplateSortIndex) ? 1 : -1;
        }).toList();
        prescriptionTabModel = prescriptionTabModel.set("MedicationModels", Immutable.List(newMedicationModels)); // Set new List
        patientStore.PatientPrescriptionTabModels[request.PatientId] = prescriptionTabModel; // Replace patient Model;
    }

    private CancelPrescribeAction(request: LocalEvent, store: PatientStore): void {
        store.PatientPrescriptionTabModels[request.PatientId] = store.PatientPrescriptionTabModels[request.PatientId].set("IsLoading", false);
    }

    private ValidateAndPrescribe(request: LocalEvent, store: PatientStore): void {
        // Save last accepted validation
        let lastAcceptedMyleActiveMedicationConflic = request.Get<string[]>("lastAcceptedMyleActiveMedicationConflic"); // From ConflicPopup
        if (lastAcceptedMyleActiveMedicationConflic != null) {
            let prescriptionTabModel = patientStore.PatientPrescriptionTabModels[request.PatientId];
            prescriptionTabModel = prescriptionTabModel.set("lastAcceptedMyleActiveMedicationConflic", lastAcceptedMyleActiveMedicationConflic);
            prescriptionTabModel = prescriptionTabModel.set("showMyleActiveMedicationConflicPopup", false); // Close conflic popup
            patientStore.PatientPrescriptionTabModels[request.PatientId] = prescriptionTabModel;
        }

        // Mark Medication for Completed Status Update
        let medicationToMarkAsCompleted = request.Get<string[]>("medicationToMarkAsCompleted");
        if (medicationToMarkAsCompleted != null) {
            this.MutatePrescriptionTabMedications("ALL", request.PatientId, med => {
                if (med.Model.DSQRenew_MyleActiveMedicationIdsConflic != null)
                    med.Model.DSQRenew_MyleActiveMedicationIdsConflicToBeMarkedAsCompleted = med.Model.DSQRenew_MyleActiveMedicationIdsConflic.filter(x => medicationToMarkAsCompleted.some(m => m == x));
            });
        }

        let medicationWithMyleActiveMedicationConflics = store.PatientPrescriptionTabModels[request.PatientId].get("MedicationModels").toArray().filter(x => x.GetConflictingActiveMedFollowingDSQRenew().length > 0).map(x => x.Model.Id);
        let AcceptedConflics = patientStore.PatientPrescriptionTabModels[request.PatientId].get("lastAcceptedMyleActiveMedicationConflic");

        if (medicationWithMyleActiveMedicationConflics.length > 0 && medicationWithMyleActiveMedicationConflics.some(c => AcceptedConflics == null || !AcceptedConflics.some(m => m == c))) {
            let prescriptionTabModel = patientStore.PatientPrescriptionTabModels[request.PatientId];
            prescriptionTabModel = prescriptionTabModel.set("showMyleActiveMedicationConflicPopup", true);
            patientStore.PatientPrescriptionTabModels[request.PatientId] = prescriptionTabModel;
        }
        else
            this.Prescribe(request, store);
    }

    public Prescribe(request: LocalEvent, store: PatientStore): void {
        const medications = store.PatientPrescriptionTabModels[request.PatientId].get("MedicationModels").toArray();
        const unWrappedMed = [];

        medications.forEach(med => {
            if (med.PrescriptionTab_OriginalMedication != null && medications.filter(y => y.Model.Id == med.PrescriptionTab_OriginalMedication.Model.Id).length == 0) {
                unWrappedMed.push(med.PrescriptionTab_OriginalMedication.Clone(false));
            }
        });

        unWrappedMed.forEach(x => {
            medications.push(x);
        });

        store.PatientPrescriptionTabModels[request.PatientId] = store.PatientPrescriptionTabModels[request.PatientId].set("IsLoading", true);

        const encounterId = store.IsMedicalItemFromOutsideNote
            ? request.Get<string>("EncounterId")
            : store.PatientPrescriptionTabModels[request.PatientId].get("EncounterId");
        const isFavorite = store.PatientPrescriptionTabModels[request.PatientId].get("IsFavorite") ?? false;
        const printCopy = request.Get<boolean>("printCopy") ?? false;
        const sendFax = request.Get<boolean>("sendFax") ?? false;
        const signCopy = request.Get<boolean>("signCopy") ?? false;
        const withSignature = request.Get<boolean>("withSignature") ?? false;

        const dsqDuplicatesConfirmations = request.Get<DSQConflitingMedication[]>("dsqDuplicatesConfirmations") ?? [];
        const prescriptionId = store.PatientPrescriptionTabModels[request.PatientId].get("prescriptionId");
        const generalInstructions = store.PatientPrescriptionTabModels[request.PatientId].get("generalInstructions");

        store.ActionsPrescriptionTab.Prescribe(medications, request.PatientId, encounterId, isFavorite, sendFax, printCopy, signCopy, withSignature, prescriptionId, dsqDuplicatesConfirmations, generalInstructions);
    }

    private CheckBoxChange(request: LocalEvent): void {
        let medicationId = request.Get<string>("arg1");
        let dosageId = request.Get<string>("arg2");
        let targetType = request.Get<PrescriptionMedicationCheckBoxes>("arg3");
        let value = request.Get<boolean>("checked");

        this.MutatePrescriptionTabMedications(medicationId, request.PatientId, medication => {
            switch (targetType) {
                case PrescriptionMedicationCheckBoxes.LongTerm:
                    medication.Model.IsLongTerm = value;
                    break;

                case PrescriptionMedicationCheckBoxes.DoNotSubstitute:
                    medication.Model.IsSubstituable = !value;
                    break;

                case PrescriptionMedicationCheckBoxes.DosageIfNeeded:
                    medication.mutateDosage(dosageId, dosage => dosage.IfNeeded = value);
                    break;
            }
        });
    }

    private TextBoxChange(request: LocalEvent): void {
        let medicationId = request.Get<string>("arg1");
        let dosageId = request.Get<string>("arg2");
        let targetType = request.Get<PrescriptionMedicationTextBoxes>("arg3");
        let unit = request.Get<MedicationDosageDisplayModeDurationUnit>("unit");
        let value = request.Get<string>("value");

        this.MutatePrescriptionTabMedications(
            medicationId,
            request.PatientId,
            (medication: MedicationModel) => {
                switch (targetType) {
                    case PrescriptionMedicationTextBoxes.Instruction:
                        medication.Model.Instruction = value;
                        break;
                    case PrescriptionMedicationTextBoxes.Renewal:
                        medication.Model.NumberOfRefill = Number(value);
                        break;
                    case PrescriptionMedicationTextBoxes.TotalDuration:
                        medication.Model.TotalDurationManual = medication.DaysToTicks(value);
                        medication.RecalculateRefillEndDate();
                        break;
                    case PrescriptionMedicationTextBoxes.MedicationName:
                        medication.Model.Name = value;
                        break;
                    case PrescriptionMedicationTextBoxes.DosageString:
                        medication.mutateDosage(dosageId, (dosage: MedicalItemMedicationAndPrescriptionDosage) => { dosage.DosageString = value; });
                        break;
                    case PrescriptionMedicationTextBoxes.DosageQuantity_BottomField:
                    case PrescriptionMedicationTextBoxes.DosageQuantity:
                        medication.mutateDosage(dosageId, (dosage: MedicalItemMedicationAndPrescriptionDosage) => { dosage.DosageServedQuantity = value; });
                        break;
                    case PrescriptionMedicationTextBoxes.DosageDuration_BottomField:
                    case PrescriptionMedicationTextBoxes.DosageDuration:
                        const valueInDays = MedicalItemMedicationAndPrescriptionDosage.getDurationValueInDays(value, unit);
                        medication.mutateDosage(dosageId, (dosage: MedicalItemMedicationAndPrescriptionDosage) => { dosage.Duration = medication.DaysToTicks(valueInDays); });
                        break;
                    case PrescriptionMedicationTextBoxes.DosageServedDays:
                        medication.mutateDosage(dosageId, (dosage: MedicalItemMedicationAndPrescriptionDosage) => { dosage.DosageServedDays = value; });
                        break;
                    case PrescriptionMedicationTextBoxes.DosageServedFrequency:
                        medication.mutateDosage(dosageId, (dosage: MedicalItemMedicationAndPrescriptionDosage) => { dosage.DosageServedFrequency = value; });
                        break;
                    case PrescriptionMedicationTextBoxes.InitialRefillQuantity:
                        medication.Model.InitialRefillQuantity = value;
                        break;
                    case PrescriptionMedicationTextBoxes.RefillQuantity:
                        medication.Model.RefillQuantity = value;
                        break;
                    case PrescriptionMedicationTextBoxes.MedicationNote: {
                        medication.Model.InternalNote = value;
                        break;
                    }
                }

                switch (targetType) {
                    case PrescriptionMedicationTextBoxes.DosageString:
                    case PrescriptionMedicationTextBoxes.DosageDuration:
                    case PrescriptionMedicationTextBoxes.DosageQuantity:
                        medication.recalculateDispensationQuantities();
                        break;
                }

                switch (targetType) {
                    case PrescriptionMedicationTextBoxes.Renewal:
                    case PrescriptionMedicationTextBoxes.DosageString:
                    case PrescriptionMedicationTextBoxes.DosageQuantity:
                    case PrescriptionMedicationTextBoxes.DosageDuration:
                        medication.RecalculateTotalDuration();
                        break;
                }
            },
            (prescriptionTab: IPrescriptionTabModel) => prescriptionTab.set("updateAllRenewalsNumber", null)
        );
    }

    private ComboBoxChange(request: LocalEvent): void {
        let medicationId = request.Get<string>("arg1");
        let dosageId = request.Get<string>("arg2");
        let targetType = request.Get<PrescriptionMedicationKendoComboBoxType>("arg3");
        let value = request.Get<string>("value");

        this.MutatePrescriptionTabMedications(medicationId, request.PatientId, (medication: MedicationModel) => {
            if (targetType == PrescriptionMedicationKendoComboBoxType.Route)
                medication.Model.Route = value;
            else if (targetType == PrescriptionMedicationKendoComboBoxType.DosageFrequency) {
                if (medication.GetDosageById(dosageId).DosageFrequency != value) {
                    medication.mutateDosage(dosageId, (d: MedicalItemMedicationAndPrescriptionDosage) => { d.DosageFrequency = value; });
                    medication.RecalculateTotalDuration();
                }
            }
            else if (targetType == PrescriptionMedicationKendoComboBoxType.DosageType)
                medication.mutateDosage(dosageId, (d: MedicalItemMedicationAndPrescriptionDosage) => { d.DosageTypeLookupId = value; });
            else if (targetType == PrescriptionMedicationKendoComboBoxType.DosageUnit)
                medication.mutateDosage(dosageId, (d: MedicalItemMedicationAndPrescriptionDosage) => { d.DosageUnit = Number(value); });

            switch (targetType) {
                case PrescriptionMedicationKendoComboBoxType.DosageFrequency:
                case PrescriptionMedicationKendoComboBoxType.DosageUnit:
                case PrescriptionMedicationKendoComboBoxType.DosageType:
                    medication.recalculateDispensationQuantities();
                    break;
            }
        });
    }

    private StartDateChange(request: LocalEvent): void {
        this.MutatePrescriptionTabMedications(
            request.Get<string>("medicationId"),
            request.PatientId,
            (medication: MedicationModel) => {
                medication.Model.RefillStartDate = request.Get<Date>("startDate");
                medication.Model.StartDatePrecision = request.Get<LifeStageDatePrecisionEnum>("startDatePrecision");
            }
        );
    }

    private DeleteDosage(request: LocalEvent): void {
        let medicationId = request.Get<string>("id");
        let dosageId = request.Get<string>("dosageid");

        this.MutatePrescriptionTabMedications(medicationId, request.PatientId, (x: MedicationModel) => {
            x.DeleteDosage(dosageId);
            x.recalculateDispensationQuantities();
        });
    }

    private AddDosage(request: LocalEvent): void {
        let medicationId = request.Get<string>("id");

        this.MutatePrescriptionTabMedications(medicationId, request.PatientId, (x: MedicationModel) => {
            x.AddNewDosage();
            x.recalculateDispensationQuantities();
        });
    }

    private SetMedicationModelDurationMode(request: LocalEvent): void {
        let medicationId = request.Get<string>("id");
        let dosageId = request.Get<string>("target");
        let mode = request.Get<MedicationDosageDisplayMode>("mode");
        let unit = request.Get<MedicationDosageDisplayModeDurationUnit>("unit");

        this.MutatePrescriptionTabMedications(medicationId, request.PatientId, (x: MedicationModel) => {
            x.SetModeForDosage(dosageId, mode, unit);
        });

        let field = "#_durationquantity" + medicationId + "_" + dosageId;
        window.setTimeout(
            () => {
                $(field).focus();
                $(field).select();
            },
            50
        );
    }

    private RemoveMedication(request: LocalEvent): void {
        let medicationId = request.Get<string>("id");

        this.MutatePrescriptionTabMedications(medicationId, request.PatientId, (x: MedicationModel) => {
            if (x.Model.PrescriptionTab_PendingStatus != null)
                this.ChangeMedicationPendingStatus(medicationId, request.PatientId, null, false);
            if (x.Model.PrescriptionTab_IsRenew)
                this.ChangeMedicationPendingStatus(x.Model.OriginalMedicationId, request.PatientId, null, false);

            if (x.PrescriptionTab_OriginalMedication != null && x.PrescriptionTab_OriginalMedication.Model.PrescriptionTab_PendingStatus != null)
                this.ChangeMedicationPendingStatus(x.PrescriptionTab_OriginalMedication.Model.Id, request.PatientId, null, false);
        });

        this.DeleteMedication(medicationId, request.PatientId);
    }

    private MedicationSelected(request: LocalEvent): void {
        let medicationResponse = request.Get<MedicationResponse>("item");

        const medicationModel = MedicationModel.FromDefaultModel(false, false);
        medicationModel.FromResponseLightUpdate(medicationResponse, false);

        this.AddMedicationModel(request.PatientId, medicationModel);

        if (medicationResponse.IsLightDTO == true) {
            medicationResponse.Id = medicationModel.Model.Id; // Make sure the reply knows which medication to Update on Callback
            patientStore.ActionsPrescriptionTab.ResolveMedicationFromSearch(medicationResponse); // Fill the rest of the information after resolving the med | but without passing the damn store.
        }
        else
            medicationModel.FromResponseFullUpdate(medicationResponse);

        this.applyGlobalAmountOfRefills(request.PatientId);
    }

    private favoriteMedicationSelected(request: LocalEvent): void {
        let self = this;
        let medicationModels = request.Get<MedicationModel[]>("item");

        MedicationModel.sortMedicationModelsByEMRTemplateSortIndex(medicationModels);
        medicationModels.forEach(x => {
            var tmpMed = x.Clone(true, true, false, true);
            tmpMed.IsInEditMode = false;
            self.AddMedicationModel(request.PatientId, tmpMed);
        });

        this.applyGlobalAmountOfRefills(request.PatientId);
    }

    private ChangeMedicationPendingStatus(medicationId: string, patientId: string, status: string, IsRenew: boolean): void {
        this.MutateMedicalSummaryMedicationsOrDSQ(medicationId, patientId, item => {
            item.Model.PrescriptionTab_PendingStatus = status;
            item.Model.PrescriptionTab_IsRenew = IsRenew;
        });
    }

    public static GetMedicationById(medicationId: string, patientId: string): MedicationModel {
        let medicalSummaryModels = patientStore.PatientMedicalSummaryModels[patientId]; // Get Patient Model
        let prescriptionModels = patientStore.PatientPrescriptionTabModels[patientId];

        let medicationItems = medicalSummaryModels.get("MedicalItemsMedicationAndPrescription").toArray(); // Get Old List
        if (prescriptionModels.get("DSQMedicationModels") != null)
            prescriptionModels.get("DSQMedicationModels").forEach(x => medicationItems.push(x)); // Add Dsq Meds if any

        let medication = medicationItems
            .toImmutable()
            .filter(x => x.Model.Id == medicationId).map(y => y)
            .first();

        return medication;
    }

    private GetPendingMedicationById(medicationId: string, patientId: string): MedicationModel {
        let prescriptionTabModel = patientStore.PatientPrescriptionTabModels[patientId]; // Get Patient Model
        let MedicationModels = prescriptionTabModel.get("MedicationModels"); // Get Old List
        let medication = MedicationModels
            .filter(x => x.Model.Id == medicationId)
            .map(y => y)
            .first();

        return medication;
    }

    private MutateMedicalSummaryMedicationsOrDSQ(medicationId: string, patientId: string, call: (item: MedicationModel) => void): void {
        let foundMatch = this.MutateMedicalSummaryMedications(medicationId, patientId, call);
        if (foundMatch == false)
            this.MutateDSQMedications(medicationId, patientId, call);
    }

    private MutateMedicalSummaryMedications(medicationId: string, patientId: string, call: (item: MedicationModel) => void): boolean {
        let foundMatch = false;

        let medicalSummaryModel = patientStore.PatientMedicalSummaryModels[patientId]; // Get Patient Model
        let MedicationModels = medicalSummaryModel.get("MedicalItemsMedicationAndPrescription"); // Get Old List

        let newMedicationModels = MedicationModels.map(x => {
            if (x.Model.Id == medicationId || medicationId == "ALL") {
                foundMatch = true;
                call(x);
            }

            return x;
        }).toList();

        medicalSummaryModel = medicalSummaryModel.set("MedicalItemsMedicationAndPrescription", Immutable.List(newMedicationModels)); // Set new List
        patientStore.PatientMedicalSummaryModels[patientId] = medicalSummaryModel; // Replace patient Model;

        return foundMatch;
    }

    private MutateDSQMedications(medicationId: string, patientId: string, call: (item: MedicationModel) => void): boolean {
        let foundMatch = false;

        let prescriptionTabModel = patientStore.PatientPrescriptionTabModels[patientId]; // Get Patient Model
        let MedicationModels = prescriptionTabModel.get("DSQMedicationModels"); // Get Old List

        let newMedicationModels = MedicationModels.map(x => {
            if (x.Model.Id == medicationId || medicationId == "ALL") {
                foundMatch = true;
                call(x);
            }

            return x;
        }).toList();

        prescriptionTabModel = prescriptionTabModel.set("DSQMedicationModels", Immutable.List(newMedicationModels)); // Set new List
        patientStore.PatientPrescriptionTabModels[patientId] = prescriptionTabModel; // Replace patient Model;

        return foundMatch;
    }

    private MutatePrescriptionTabMedications(medicationId: string, patientId: string, call: (med: MedicationModel) => void, prescriptionTabMutation: (perscriptionTab: IPrescriptionTabModel) => IPrescriptionTabModel = null): void {
        let prescriptionTabModel = patientStore.PatientPrescriptionTabModels[patientId]; // Get Patient Model
        let medicationModels = prescriptionTabModel.get("MedicationModels"); // Get Old List

        let newMedicationModels = medicationModels.map(x => {
            if (x.Model.Id == medicationId || medicationId == "ALL") {
                call(x);
            }
            return x;
        }).toList();
        prescriptionTabModel = prescriptionTabModel.set("MedicationModels", Immutable.List(newMedicationModels)); // Set new List

        if (prescriptionTabMutation) {
            prescriptionTabModel = prescriptionTabMutation(prescriptionTabModel);
        }
        patientStore.PatientPrescriptionTabModels[patientId] = prescriptionTabModel; // Replace patient Model;
    }

    private DeleteMedication(medicationId: string, patientId: string): void {
        let prescriptionTabModel = patientStore.PatientPrescriptionTabModels[patientId]; // Get Patient Model
        let MedicationModels = prescriptionTabModel.get("MedicationModels"); // Get Old List

        let newMedicationModels = MedicationModels.filter(x => {
            if (x.Model.Id == medicationId)
                return false;

            return true;
        }).toList();

        prescriptionTabModel = prescriptionTabModel.set("MedicationModels", Immutable.List(newMedicationModels)); // Set new List
        patientStore.PatientPrescriptionTabModels[patientId] = prescriptionTabModel; // Replace patient Model;

        this.RefreshNumberOfAlertsVigilance(patientId);
    }

    private AddMedicationModel(patientId: string, medicationModel: MedicationModel, switchOthersToPreview: boolean = true): void {
        this.MutatePrescriptionTabMedications("ALL", patientId, (x: MedicationModel) => {
            if (switchOthersToPreview)
                x.IsInEditMode = false;
        });
        this.AddMedicationRaw(patientId, medicationModel);
    }

    private AddMedicationRaw(patientId: string, medication: MedicationModel): void {
        let prescriptionTabModel = patientStore.PatientPrescriptionTabModels[patientId]; // Get Patient Model
        let medicationModels = prescriptionTabModel.get("MedicationModels"); // Get Old List

        // position the new item in first position
        medicationModels.forEach(x => {
            x.EMRTemplateSortIndexSet(x.Model.EMRTemplateSortIndex + 1);
        });
        medication.EMRTemplateSortIndexSet(0);

        let newMedicationModels = medicationModels.reverse().toList().push(medication).reverse().toList();
        prescriptionTabModel = prescriptionTabModel.set("MedicationModels", newMedicationModels); // Set new List
        patientStore.PatientPrescriptionTabModels[patientId] = prescriptionTabModel; // Replace patient Model;

        this.RefreshNumberOfAlertsVigilance(patientId);
    }

    public RefreshNumberOfAlertsVigilance(PatientContactId: string): void {
        if (patientStore.vigilanceAlertsDebounceTimeoutReference != null) {
            clearTimeout(patientStore.vigilanceAlertsDebounceTimeoutReference);
            patientStore.vigilanceAlertsDebounceTimeoutReference = null;
        }

        patientStore.vigilanceAlertsDebounceTimeoutReference =
            window.setTimeout(
                () => {
                    const eventId = GuidHelper.NewGuid();
                    let prescriptionTabModel = patientStore.PatientPrescriptionTabModels[PatientContactId];

                    prescriptionTabModel = prescriptionTabModel
                        .set("LastVigilanceAlertEvent", eventId)
                        .set("VigilanceNumberOfAlerts", null)
                        .set("VigilanceSevereAlertLevel", null);

                    patientStore.PatientPrescriptionTabModels[PatientContactId] = prescriptionTabModel;
                    patientStore.EmitChangeToListeners();

                    const medicationModels = prescriptionTabModel.get("MedicationModels");

                    let newDins = medicationModels
                        .filter(p => p.Model.PrescriptionTab_PendingStatus == null)
                        .map(p => p.Model.DIN)
                        .toArray();
                    let interuptedDins = medicationModels
                        .filter(p => p.Model.PrescriptionTab_PendingStatus != null)
                        .map(p => p.Model.DIN)
                        .toArray();

                    patientStore.ActionsPrescriptionTab.RefreshVigilanceAlerts(PatientContactId, newDins, interuptedDins, eventId);
                },
                300
            );
    }

    public GetVigilanceAnalysis(request: LocalEvent): void {
        let prescriptionTabModel = patientStore.PatientPrescriptionTabModels[request.PatientId];
        patientStore.PatientPrescriptionTabModels[request.PatientId] = prescriptionTabModel;

        let MedicationModels = prescriptionTabModel.get("MedicationModels");

        var NewDINs = MedicationModels.filter(y => y.Model.PrescriptionTab_PendingStatus == null).map(x => x.Model.DIN).toArray();
        var InteruptedDINs = MedicationModels.filter(y => y.Model.PrescriptionTab_PendingStatus != null).map(x => x.Model.DIN).toArray();

        patientStore.ActionsPrescriptionTab.GetVigilanceAnalysis(request.PatientId, NewDINs, InteruptedDINs);
    }

    private updateAllRenewalsNumber(request: LocalEvent): void {
        const numberOfRenewals = Number(request.Get<string>("value"));
        patientStore.PatientPrescriptionTabModels[request.PatientId] = patientStore.PatientPrescriptionTabModels[request.PatientId].set("updateAllRenewalsNumber", numberOfRenewals);
        this.applyGlobalAmountOfRefills(request.PatientId);
    }

    private applyGlobalAmountOfRefills(patientId: string): void {
        if (patientStore.PatientPrescriptionTabModels[patientId].get("updateAllRenewalsNumber")) {
            this.MutatePrescriptionTabMedications("ALL", patientId, prescription => {
                prescription.SetRefillNumber(patientStore.PatientPrescriptionTabModels[patientId].get("updateAllRenewalsNumber"));
            });
        }
    }

    private SaveGeneralInstructions(request: LocalEvent, store: PatientStore): void {
        const generalInstructions = request.Get<string>("generalInstructions");
        let prescriptionTabModel = store.PatientPrescriptionTabModels[request.PatientId];
        prescriptionTabModel = prescriptionTabModel.set("generalInstructions", generalInstructions);
        store.PatientPrescriptionTabModels[request.PatientId] = prescriptionTabModel;
    }

    private saveIsHeaderExpanded(request: LocalEvent, store: PatientStore): void {
        const isHeaderExpanded: boolean = request.Get<boolean>("isHeaderExpanded");
        store.PatientPrescriptionTabModels[request.PatientId] = store.PatientPrescriptionTabModels[request.PatientId]
            .set("isHeaderExpanded", isHeaderExpanded);
    }
}

export { PatientPrescriptionTabHandler };
